import { ReactNode } from "react";
import {
  CalendarIcon,
  CheckIcon,
  ClockIcon,
  SewingPinIcon,
} from "@radix-ui/react-icons";
import { OrganizationPublicResponse } from "client/openapi";
import moment from "moment-timezone";
import { PersonPhoto } from "./PublicShell";
import { calendarLinks, phoneLabel, zoneLabel } from "./publicBooking";

export type BookingSummary = {
  title: string; // "Algebra with Jordan Ellis"
  tutorFirstName: string;
  tutorName: string;
  tutorPhoto?: string | null;
  start: string;
  minutes: number;
  place: string;
  zone: string;
};

export function FactRow({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <p className="flex items-center gap-3 text-[15px] text-gray-700">
      <span className="text-gray-400">{icon}</span>
      {children}
    </p>
  );
}

/** "Thursday, October 8" and "4:30 to 5:30pm" in the visitor's zone. */
export function whenLines(summary: BookingSummary) {
  const start = moment.tz(summary.start, summary.zone);
  const end = start.clone().add(summary.minutes, "minutes");
  const sameHalf = start.format("a") === end.format("a");
  return {
    day: start.format("dddd, MMMM D"),
    time: `${start.format(sameHalf ? "h:mm" : "h:mma")} to ${end.format(
      "h:mma"
    )}`,
  };
}

/** Request sent (a tutor must accept) or booked, with add to calendar. */
export default function BookingDone({
  summary,
  status,
  studentName,
  org,
  accent,
  onBookAnother,
}: {
  summary: BookingSummary;
  status: "requested" | "booked";
  studentName: string;
  org: OrganizationPublicResponse;
  accent: string;
  onBookAnother: () => void;
}) {
  const { day, time } = whenLines(summary);
  const links = calendarLinks({
    title: summary.title,
    start: summary.start,
    minutes: summary.minutes,
    location: summary.place,
    details: `${summary.title} for ${studentName}. Booked with ${org.name}.`,
  });
  const linkClass =
    "rounded-lg border border-gray-300 px-3 py-2 text-center text-sm font-semibold text-gray-700 hover:bg-gray-50";

  return (
    <div className="mx-auto max-w-md px-5 py-10 text-center">
      <div
        className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
        style={{ backgroundColor: `${accent}1F`, color: accent }}
      >
        <CheckIcon width={30} height={30} />
      </div>
      <h1 className="mt-4 text-2xl font-bold">
        {status === "requested" ? "Request sent" : "You're booked"}
      </h1>
      <p className="mt-2 text-gray-600">
        {status === "requested"
          ? `${summary.tutorFirstName} will confirm soon.`
          : `${summary.tutorFirstName} will see you then.`}
      </p>

      <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4 text-left">
        <div className="flex items-center gap-3 border-b border-gray-200 pb-3">
          <PersonPhoto
            url={summary.tutorPhoto}
            name={summary.tutorName}
            size={44}
            accent={accent}
          />
          <div>
            <p className="font-semibold">{summary.title}</p>
            <p className="text-sm text-gray-500">for {studentName}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-col gap-2">
          <FactRow icon={<CalendarIcon />}>{day}</FactRow>
          <FactRow icon={<ClockIcon />}>
            {time}, {zoneLabel(summary.zone)}
          </FactRow>
          <FactRow icon={<SewingPinIcon />}>{summary.place}</FactRow>
        </div>
      </div>

      <p className="mt-5 text-xs font-semibold text-gray-600">
        Add to your calendar
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {[
          { label: "Google", href: links.google },
          { label: "Apple", href: links.apple, download: "session.ics" },
          { label: "Outlook.com", href: links.outlook },
          { label: "Microsoft 365", href: links.office },
        ].map((link) => (
          <a
            key={link.label}
            className={linkClass}
            href={link.href}
            download={link.download}
            target={link.download ? undefined : "_blank"}
            rel="noreferrer"
          >
            {link.label}
          </a>
        ))}
      </div>

      <button
        type="button"
        onClick={onBookAnother}
        className="mt-6 w-full rounded-lg border-2 py-3 font-semibold"
        style={{ borderColor: accent, color: accent }}
      >
        Book another session
      </button>
      {org.public_phone_number && (
        <p className="mt-3 text-xs text-gray-500">
          Need to change it? Call {org.name} at{" "}
          {phoneLabel(org.public_phone_number)}.
        </p>
      )}
    </div>
  );
}
